/**
 * 1.7.2 rough cuts (POST /api/v1/sequences/rough-cut, the MCP tool create_rough_cut): a first cut an assistant or any
 * API client asks for, made as a NEW sequence for a person to finish in the editor. Never an edit of an existing one.
 *
 *  - the request (RoughCutRequestSchema): the storyline clips in order, cutaways over them on a "Cutaways" video track with
 *    their sound muted, music on A2, graphics with their fields filled in, and markers. Times are seconds from the
 *    start of the media, as the MCP tools report them; a place in the sequence is `{seconds}` or, easier for an
 *    assistant, `{clip, offsetSeconds}` from a storyline clip's start (clip 1 is the first);
 *  - `buildRoughCut` (pure): the document, made only with the editor's own `ops`, so it is exactly what the editor
 *    would make, and the warnings (a range clamped to the media or to what is recorded so far, a clip that came out
 *    zero-length, a cutaway without a free video track, an unknown field, a value cut to its field's length…).
 *
 * The server resolves the assets and templates (RoughCutSource, RoughCutTemplate) and validates the document as a
 * save does (sequences.ts validateDocument).
 */
import { z } from 'zod';
import { TICKS_PER_SECOND, snapFrameRate, ticksPerFrame, type Rational } from './time.ts';
import {
  MARKER_COLORS, MAX_LAYERS, MAX_MARKERS, clipDuration, emptySequence, layoutVideo, sequenceDuration, snap, type SequenceDocument,
} from './sequence.ts';
import { newId, ops, type SourceRange } from './sequence-ops.ts';
import { SEQUENCE_FORMATS, sequenceSizeProblem } from './sequence-formats.ts';
import { msToFrameTicks, ografDefaultLengthMs, ografDefaults, ografFields, ografIsSting, type GraphicValue, type OgrafManifest } from './ograf.ts';
import type { TemplateManifest } from './graphics.ts';

/** How much one rough cut may hold. */
export const ROUGH_CUT_LIMITS = { clips: 200, cutaways: 100, music: 10, graphics: 50, markers: 300 } as const;
/** A still's length on the storyline or a layer when none is given, in seconds. */
export const ROUGH_CUT_STILL_SECONDS = 5;
/** Music's level on A2 when none is given (under speech). */
export const ROUGH_CUT_MUSIC_GAIN_DB = -12;
/** The video track cutaways go on (then "Cutaways 2", … on an overlap). */
export const CUTAWAY_LAYER = 'Cutaways';

const Id = z.string().uuid();
/** Seconds from the start of the media (or of the sequence), at most 24 hours. */
const Seconds = z.number().min(0).max(86_400);
const Note = z.string().trim().min(1).max(200);

/** A place in the sequence: seconds from its start, or seconds from a storyline clip's start (clip 1 is the first). */
export const RoughCutAtSchema = z.union([
  z.object({ seconds: Seconds }).strict(),
  z.object({ clip: z.number().int().min(1).max(ROUGH_CUT_LIMITS.clips), offsetSeconds: z.number().min(-3600).max(86_400).default(0) }).strict(),
]);
export type RoughCutAt = z.infer<typeof RoughCutAtSchema>;

export const RoughCutClipSchema = z.object({
  assetId: Id,
  /** Default 0. */
  inSeconds: Seconds.optional(),
  /** Default: the end of the media (what is recorded so far, for a recording). */
  outSeconds: Seconds.optional(),
  /** A still's length (default 5 s); ignored for video. */
  durationSeconds: z.number().min(0.04).max(3600).optional(),
  /** Becomes a marker at the clip's start. */
  note: Note.optional(),
  markerColor: z.enum(MARKER_COLORS).optional(),
}).strict();

export const RoughCutCutawaySchema = z.object({
  assetId: Id,
  inSeconds: Seconds.optional(),
  outSeconds: Seconds.optional(),
  durationSeconds: z.number().min(0.04).max(3600).optional(),
  at: RoughCutAtSchema,
  /** Becomes a marker where the cutaway starts. */
  note: Note.optional(),
}).strict();

export const RoughCutMusicSchema = z.object({
  assetId: Id,
  inSeconds: Seconds.optional(),
  outSeconds: Seconds.optional(),
  /** Default: the start of the sequence. */
  at: RoughCutAtSchema.default({ seconds: 0 }),
  gainDb: z.number().min(-60).max(12).default(ROUGH_CUT_MUSIC_GAIN_DB),
}).strict();

export const RoughCutGraphicSchema = z.object({
  /** list_graphics' templateId (a graphics asset's is asset-<asset id>); its latest enabled, ready version is used. */
  templateId: z.string().min(1).max(80),
  at: RoughCutAtSchema,
  /** Default: the graphic's own length (in + the workspace's hold + out); a sting keeps its fixed length. */
  durationSeconds: z.number().min(0.04).max(3600).optional(),
  /** Field values by the field's id or label; fields left out keep their defaults. */
  fields: z.record(z.string().min(1).max(120), z.union([z.string().max(2000), z.number(), z.boolean()])).default({}),
}).strict();

export const RoughCutMarkerSchema = z.object({ at: RoughCutAtSchema, label: z.string().trim().min(1).max(200), color: z.enum(MARKER_COLORS).optional() }).strict();

/** The sequence's frame: a SEQUENCE_FORMATS id ("hd1080-25"), or a size and a frame rate (fps, or num/den). */
export const RoughCutFormatSchema = z.union([
  z.string().min(1).max(40),
  z.object({
    width: z.number().int().min(16).max(7680),
    height: z.number().int().min(16).max(4320),
    frameRate: z.union([z.number().min(1).max(120), z.object({ num: z.number().int().positive(), den: z.number().int().positive() })]),
  }).strict(),
]);

export const RoughCutRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().max(1500).optional(),
  format: RoughCutFormatSchema.optional(),
  clips: z.array(RoughCutClipSchema).min(1).max(ROUGH_CUT_LIMITS.clips),
  cutaways: z.array(RoughCutCutawaySchema).max(ROUGH_CUT_LIMITS.cutaways).default([]),
  music: z.array(RoughCutMusicSchema).max(ROUGH_CUT_LIMITS.music).default([]),
  graphics: z.array(RoughCutGraphicSchema).max(ROUGH_CUT_LIMITS.graphics).default([]),
  markers: z.array(RoughCutMarkerSchema).max(ROUGH_CUT_LIMITS.markers).default([]),
  /** File the new sequence into this collection (one the caller may change). */
  collectionId: Id.optional(),
}).strict();
export type RoughCutRequest = z.output<typeof RoughCutRequestSchema>;
export type RoughCutRequestInput = z.input<typeof RoughCutRequestSchema>;

export interface RoughCutCounts { clips: number; cutaways: number; music: number; graphics: number; markers: number }

/** POST /api/v1/sequences/rough-cut's answer (201): the sequence summary plus these. */
export interface RoughCutResultDTO {
  id: string;
  name: string;
  description: string;
  revision: number;
  durationTicks: number;
  frameRate: Rational;
  width: number;
  height: number;
  /** The editor page (PRISM_PUBLIC_URL + /create/<id>). */
  url: string;
  durationSeconds: number;
  counts: RoughCutCounts;
  warnings: string[];
  collectionId: string | null;
}

/** An asset's current version as the builder needs it (the server resolves and checks it first). */
export interface RoughCutSource {
  assetId: string;
  versionId: string;
  title: string;
  mediaKind: 'video' | 'audio' | 'image';
  /** The media's length; for a recording, what is recorded so far. Null: unknown (stills). */
  durationTicks: number | null;
  recording: boolean;
  frameRate: Rational | null;
  /** The displayed picture size (after rotation), for Fit or Fill (framing.ts defaultFit). */
  width: number | null;
  height: number | null;
}

/** A template's latest enabled, ready version. */
export interface RoughCutTemplate {
  templateId: string;
  version: number;
  name: string;
  engine: 'renderat' | 'ograf-v1';
  /** renderAt: the manifest with the asset's field defaults; OGraf: PRISM's summary (unused, `ograf` rules). */
  manifest: TemplateManifest;
  ograf: OgrafManifest | null;
}

/** A field a graphic takes, as list_graphics shows it and the builder matches it (by id or label). */
export interface RoughCutField {
  id: string;
  label: string;
  kind: string;
  default: GraphicValue | null;
  options?: GraphicValue[];
  maxLength?: number;
  required: boolean;
}

/** The fields of a template (OGraf: its schema's editable properties; renderAt: the manifest's fields). */
export function roughCutFields(t: Pick<RoughCutTemplate, 'engine' | 'manifest' | 'ograf'>): RoughCutField[] {
  if (t.engine === 'ograf-v1' && t.ograf) {
    return ografFields(t.ograf).filter((f) => f.kind !== 'unsupported').map((f) => ({
      id: f.id, label: f.title, kind: f.kind, default: f.default ?? null,
      ...(f.options ? { options: f.options } : {}), ...(f.maxLength !== undefined ? { maxLength: f.maxLength } : {}), required: false,
    }));
  }
  return t.manifest.fields.map((f) => ({ id: f.id, label: f.label, kind: f.type, default: f.default, ...(f.maxLength ? { maxLength: f.maxLength } : {}), required: f.required }));
}

/** A template's length in seconds when placed with no length given, and whether that length is fixed (a sting). */
export function roughCutGraphicLength(t: Pick<RoughCutTemplate, 'engine' | 'manifest' | 'ograf'>, holdSeconds: number): { seconds: number; fixed: boolean } {
  if (t.engine === 'ograf-v1' && t.ograf) return { seconds: ografDefaultLengthMs(t.ograf, holdSeconds) / 1000, fixed: ografIsSting(t.ograf) };
  return { seconds: t.manifest.defaultDurationSeconds, fixed: false };
}

/** A request the builder cannot make anything of (an unknown format): the server answers 422 with it. */
export class RoughCutProblem extends Error {
  readonly code: 'VALIDATION_FAILED' | 'INVALID_SIZE';
  readonly field: string | undefined;
  constructor(code: 'VALIDATION_FAILED' | 'INVALID_SIZE', message: string, field?: string) {
    super(message);
    this.code = code;
    this.field = field;
  }
}

const ticksOf = (seconds: number) => Math.round(seconds * TICKS_PER_SECOND);
const secondsLabel = (ticks: number) => `${Math.round((ticks / TICKS_PER_SECOND) * 100) / 100} s`;

/** The sequence's frame: the request's format, else 1920×1080 at the first video clip's frame rate (or 25 fps). */
export function roughCutFrame(req: Pick<RoughCutRequest, 'format' | 'clips'>, sources: ReadonlyMap<string, RoughCutSource>): { width: number; height: number; frameRate: Rational } {
  const f = req.format;
  if (typeof f === 'string') {
    const preset = SEQUENCE_FORMATS.find((p) => p.id === f);
    if (!preset) throw new RoughCutProblem('VALIDATION_FAILED', `There is no format “${f}”; use one of ${SEQUENCE_FORMATS.slice(0, 6).map((p) => p.id).join(', ')}… or {width, height, frameRate}`, 'format');
    return { width: preset.width, height: preset.height, frameRate: preset.frameRate };
  }
  if (f) {
    const problem = sequenceSizeProblem(f.width, f.height);
    if (problem) throw new RoughCutProblem('INVALID_SIZE', problem, 'format');
    return { width: f.width, height: f.height, frameRate: typeof f.frameRate === 'number' ? snapFrameRate(f.frameRate) : snapFrameRate(f.frameRate.num / f.frameRate.den) };
  }
  const first = req.clips.map((c) => sources.get(c.assetId)).find((s) => s?.mediaKind === 'video' && s.frameRate);
  return { width: 1920, height: 1080, frameRate: first?.frameRate ? snapFrameRate(first.frameRate.num / first.frameRate.den) : { num: 25, den: 1 } };
}

/**
 * A source range from seconds: clamped to the media (or what is recorded so far), with a warning when it was; a still
 * gets its length (default 5 s). Null (with a warning) when nothing is left.
 */
function rangeOf(
  what: string, src: RoughCutSource, item: { inSeconds?: number; outSeconds?: number; durationSeconds?: number }, rate: Rational, warn: (w: string) => void,
): SourceRange | null {
  const base = { assetId: src.assetId, versionId: src.versionId, label: src.title.slice(0, 200), width: src.width, height: src.height };
  if (src.mediaKind === 'image') {
    const len = ticksOf(item.durationSeconds ?? (item.inSeconds !== undefined && item.outSeconds !== undefined && item.outSeconds > item.inSeconds ? item.outSeconds - item.inSeconds : ROUGH_CUT_STILL_SECONDS));
    return { ...base, sourceInTicks: 0, sourceOutTicks: len, sourceDurationTicks: len };
  }
  const length = src.durationTicks;
  let inT = ticksOf(item.inSeconds ?? 0);
  let outT = item.outSeconds !== undefined ? ticksOf(item.outSeconds) : length;
  if (outT === null) { warn(`${what} (“${src.title}”) has no known length; give outSeconds. Left out.`); return null; }
  if (length !== null) {
    const limit = src.recording ? 'what is recorded so far' : 'the media';
    if (inT > length) { warn(`${what} (“${src.title}”) starts after the end of ${limit} (${secondsLabel(length)}). Left out.`); return null; }
    if (outT > length + ticksPerFrame(rate)) warn(`${what} (“${src.title}”) ended after ${limit}; it ends at ${secondsLabel(length)} instead.`);
    outT = Math.min(outT, length);
  }
  inT = Math.max(0, inT);
  if (snap(outT, rate) - snap(inT, rate) <= 0) { warn(`${what} (“${src.title}”) came out zero-length. Left out.`); return null; }
  return { ...base, sourceInTicks: inT, sourceOutTicks: outT, sourceDurationTicks: length ?? outT };
}

/** The values of a graphic: its defaults, then the request's by id or label (unknown ones and long text with warnings). */
function graphicData(what: string, t: RoughCutTemplate, given: Record<string, GraphicValue>, warn: (w: string) => void): Record<string, GraphicValue> {
  const fields = roughCutFields(t);
  const data: Record<string, GraphicValue> = t.engine === 'ograf-v1' && t.ograf
    ? ografDefaults(t.ograf)
    : Object.fromEntries(t.manifest.fields.map((f) => [f.id, f.default]));
  const norm = (s: string) => s.trim().toLowerCase();
  for (const [key, raw] of Object.entries(given)) {
    const f = fields.find((x) => x.id === key) ?? fields.find((x) => norm(x.label) === norm(key)) ?? fields.find((x) => norm(x.id) === norm(key));
    if (!f) { warn(`${what} (“${t.name}”) has no field “${key}”; its fields are ${fields.map((x) => x.label).join(', ') || 'none'}. It was left out.`); continue; }
    // renderAt templates take text only.
    let v: GraphicValue = t.engine === 'renderat' && typeof raw !== 'string' ? String(raw) : raw;
    if (typeof v === 'string' && f.maxLength !== undefined && v.length > f.maxLength) {
      warn(`${what} (“${t.name}”): ${f.label} was cut to ${f.maxLength} characters.`);
      v = v.slice(0, f.maxLength);
    }
    data[f.id] = v;
  }
  return data;
}

/**
 * The rough cut's document and warnings. `sources` has every asset the request names (by asset id), `templates` every
 * template (by template id): the server checks both first. `holdSeconds` is the workspace's graphics hold.
 */
export function buildRoughCut(
  req: RoughCutRequest, sources: ReadonlyMap<string, RoughCutSource>, templates: ReadonlyMap<string, RoughCutTemplate>, holdSeconds: number,
): { document: SequenceDocument; warnings: string[]; counts: RoughCutCounts } {
  const warnings: string[] = [];
  const warn = (w: string) => { warnings.push(w); };
  const frame = roughCutFrame(req, sources);
  const rate = frame.frameRate;
  let doc = emptySequence(rate, frame.width, frame.height);
  const counts: RoughCutCounts = { clips: 0, cutaways: 0, music: 0, graphics: 0, markers: 0 };
  const source = (id: string) => {
    const s = sources.get(id);
    if (!s) throw new Error(`rough cut: asset ${id} was not resolved`);
    return s;
  };

  // The storyline, in order; the request's clip n is the n-th kept clip (or null when it was left out).
  const kept: Array<{ id: string; note?: string; color?: (typeof MARKER_COLORS)[number] } | null> = [];
  req.clips.forEach((c, i) => {
    const what = `Clip ${i + 1}`;
    const src = source(c.assetId);
    if (src.mediaKind === 'audio') { warn(`${what} (“${src.title}”) is sound only; give it as music. Left out.`); kept.push(null); return; }
    const range = rangeOf(what, src, c, rate, warn);
    if (!range) { kept.push(null); return; }
    const before = doc;
    doc = ops.insertClip(doc, range);
    if (doc === before) { warn(`${what} (“${src.title}”) came out zero-length. Left out.`); kept.push(null); return; }
    kept.push({ id: doc.video.at(-1)!.id, ...(c.note ? { note: c.note } : {}), ...(c.markerColor ? { color: c.markerColor } : {}) });
    counts.clips++;
  });

  // Where the storyline clips landed (gaps and transitions are none here, so this is final).
  const placed = new Map(layoutVideo(doc).map((p) => [p.clip.id, p.recordIn]));
  const at = (what: string, a: RoughCutAt): number | null => {
    if ('seconds' in a) return snap(ticksOf(a.seconds), rate);
    const k = kept[a.clip - 1];
    if (a.clip > kept.length) { warn(`${what} is placed on clip ${a.clip}, but there are ${kept.length} clips. Left out.`); return null; }
    if (!k) { warn(`${what} is placed on clip ${a.clip}, which was left out. Left out too.`); return null; }
    return snap(Math.max(0, placed.get(k.id)! + ticksOf(a.offsetSeconds)), rate);
  };

  const marker = (atTicks: number, label: string, color?: (typeof MARKER_COLORS)[number]) => {
    if ((doc.markers ?? []).length >= MAX_MARKERS) { warn(`Marker “${label}” was left out: a sequence has at most ${MAX_MARKERS} markers.`); return; }
    const id = newId('k');
    doc = ops.addMarker(doc, atTicks, label, id);
    if (color) doc = ops.updateMarker(doc, id, { color });
    counts.markers++;
  };
  for (const k of kept) if (k?.note) marker(placed.get(k.id)!, k.note, k.color);

  // Cutaways: on "Cutaways", the next free cutaway track on an overlap (a new one while there is room), sound muted.
  req.cutaways.forEach((c, i) => {
    const what = `Cutaway ${i + 1}`;
    const src = source(c.assetId);
    if (src.mediaKind === 'audio') { warn(`${what} (“${src.title}”) is sound only; a cutaway is a picture. Left out.`); return; }
    const start = at(what, c.at);
    if (start === null) return;
    const range = rangeOf(what, src, c, rate, warn);
    if (!range) return;
    const len = snap(range.sourceOutTicks, rate) - snap(range.sourceInTicks, rate);
    const free = (l: SequenceDocument['layers'][number]) => l.clips.every((x) => start >= x.recordInTicks + clipDuration(x) || start + len <= x.recordInTicks);
    let layer = doc.layers.find((l) => (l.label === CUTAWAY_LAYER || l.label.startsWith(`${CUTAWAY_LAYER} `)) && free(l));
    if (!layer) {
      if (doc.layers.length >= MAX_LAYERS) { warn(`${what} (“${src.title}”) overlaps other cutaways and there is no free video track (at most ${MAX_LAYERS} above V1). Left out.`); return; }
      const n = doc.layers.filter((l) => l.label === CUTAWAY_LAYER || l.label.startsWith(`${CUTAWAY_LAYER} `)).length;
      doc = ops.addLayer(doc, n ? `${CUTAWAY_LAYER} ${n + 1}` : CUTAWAY_LAYER);
      layer = doc.layers.at(-1)!;
    }
    const layerId = layer.id;
    const before = doc;
    doc = ops.addLayerClip(doc, range, start, layerId);
    if (doc === before) { warn(`${what} (“${src.title}”) came out zero-length. Left out.`); return; }
    const clip = doc.layers.find((l) => l.id === layerId)!.clips.at(-1)!;
    doc = ops.updateClip(doc, clip.id, { muted: true });
    counts.cutaways++;
    if (c.note) marker(start, c.note);
  });

  // Music on A2, where asked (no overlaps there).
  req.music.forEach((m, i) => {
    const what = `Music ${i + 1}`;
    const src = source(m.assetId);
    if (src.mediaKind === 'image') { warn(`${what} (“${src.title}”) is a still, with no sound. Left out.`); return; }
    const start = at(what, m.at);
    if (start === null) return;
    const range = rangeOf(what, src, m, rate, warn);
    if (!range) return;
    const before = doc;
    doc = ops.addAudio(doc, range, start, { exact: true });
    if (doc === before) { warn(`${what} (“${src.title}”) overlaps other music on A2. Left out.`); return; }
    doc = ops.updateClip(doc, doc.audio.at(-1)!.id, { gainDb: m.gainDb });
    counts.music++;
  });

  // Graphics: the template's latest version, its fields by id or label, its own length (a sting's is fixed).
  req.graphics.forEach((g, i) => {
    const what = `Graphic ${i + 1}`;
    const t = templates.get(g.templateId);
    if (!t) throw new Error(`rough cut: template ${g.templateId} was not resolved`);
    const start = at(what, g.at);
    if (start === null) return;
    const length = roughCutGraphicLength(t, holdSeconds);
    if (length.fixed && g.durationSeconds !== undefined) warn(`${what} (“${t.name}”) is a sting with a fixed length of ${length.seconds} s; durationSeconds was ignored.`);
    const seconds = length.fixed ? length.seconds : g.durationSeconds ?? length.seconds;
    const durationTicks = t.engine === 'ograf-v1' ? msToFrameTicks(seconds * 1000, rate) : Math.max(ticksPerFrame(rate), snap(ticksOf(seconds), rate));
    // 1.7.3: graphics on one track never overlap; one that would goes on the next graphics track free at its time
    // (GFX 2, GFX 3), so it keeps its time; with all three in use, addGraphic moves it to the nearest free time on GFX 1.
    const taken = (k: number) => doc.graphics.filter((x) => (x.track ?? 0) === k).some((x) => x.recordInTicks < start + durationTicks && start < x.recordInTicks + x.durationTicks);
    const track = ([0, 1, 2] as const).find((k) => !taken(k)) ?? 0;
    const before = doc;
    doc = ops.addGraphic(doc, { templateId: t.templateId, templateVersion: t.version, recordInTicks: start, durationTicks, data: graphicData(what, t, g.fields, warn), ...(track ? { track } : {}) });
    const added = doc.graphics.at(-1);
    if (doc !== before && added && added.recordInTicks !== snap(start, rate)) warn(`${what} (“${t.name}”) overlapped other graphics on every graphics track; it was moved to the nearest free time.`);
    counts.graphics++;
  });

  for (const [i, m] of req.markers.entries()) {
    const start = at(`Marker ${i + 1}`, m.at);
    if (start !== null) marker(start, m.label, m.color);
  }

  if (!doc.video.length) warn('No clip made it to the storyline.');
  return { document: doc, warnings, counts };
}

/** The sequence's length in seconds (two decimals), for the answer. */
export const roughCutSeconds = (doc: SequenceDocument): number => Math.round((sequenceDuration(doc) / TICKS_PER_SECOND) * 100) / 100;
